import { useEffect, useRef, useState } from 'react';
import { useLang } from '@/hooks/useLang';

const copy = {
  EN: {
    tag: 'AI-Powered Production Platform',
    headline1: 'AI-powered films,',
    headline2: 'series, and training',
    headline3: 'content.',
    sub: 'A new generation of content creation — powered by artificial intelligence.',
    cta1: 'Start a Project',
    cta2: 'Explore Work',
  },
  TR: {
    tag: 'Yapay Zeka Destekli Prodüksiyon Platformu',
    headline1: 'Yapay zeka destekli',
    headline2: 'film, dizi ve eğitim',
    headline3: 'içerikleri.',
    sub: 'Yapay zeka ile güçlendirilmiş yeni nesil içerik üretimi.',
    cta1: 'Proje Başlat',
    cta2: 'Çalışmaları Gör',
  },
};

export default function HeroSection() {
  const lang = useLang();
  const t = copy[lang];
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen bg-white flex items-center overflow-hidden pt-24"
    >
      {/* Subtle background gradient blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 70%)',
            transform: 'translate(20%, -20%)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(181,55,255,0.04) 0%, transparent 70%)',
            transform: 'translate(-20%, 20%)',
          }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative w-full max-w-[1400px] mx-auto px-8 md:px-16 py-16">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-20">
          {/* Left: Text */}
          <div
            className={`flex-1 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Tag */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full mb-8">
              <span className="w-2 h-2 rounded-full bg-gray-900 inline-block"></span>
              <span className="text-xs font-semibold text-gray-600 tracking-widest uppercase">
                {t.tag}
              </span>
            </div>

            {/* Headline */}
            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.05] tracking-tight mb-6"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {t.headline1}
              <br />
              {t.headline2}
              <br />
              <span className="text-gray-400">{t.headline3}</span>
            </h1>

            {/* Subtext */}
            <p className="text-lg text-gray-500 font-light leading-relaxed max-w-md mb-10">
              {t.sub}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  handleNav('#contact');
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-900 text-white text-sm font-semibold tracking-wide rounded-full hover:bg-gray-700 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {t.cta1}
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <a
                href="#showreel"
                onClick={(e) => {
                  e.preventDefault();
                  handleNav('#showreel');
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gray-200 text-gray-700 text-sm font-semibold tracking-wide rounded-full hover:border-gray-400 hover:text-gray-900 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                {t.cta2}
              </a>
            </div>
          </div>

          {/* Right: Custom MP4 Video */}
          <div
            className={`flex-1 w-full transition-all duration-1000 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gray-900 border border-gray-100">
              <video
                ref={videoRef}
                src="https://storage.readdy-site.link/project_files/1460d35d-0d85-434d-9ed7-7e4515871db4/56a8bcc4-0ed0-40a2-8fda-926de3de43e7_kodo-loop-vid.mp4?v=d98383a354b2edf65aacfd6ab43df4b5"
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
              {/* Subtle vignette overlay */}
              <div className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.15) 100%)',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs text-gray-400 tracking-widest uppercase font-medium">Scroll</span>
        <div className="w-px h-8 bg-gray-300"></div>
      </div>
    </section>
  );
}
